/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 */
function initLazyScript(element, callback, threshold = 500) {
  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (typeof callback === "function") {
              callback();
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { rootMargin: `0px 0px ${threshold}px 0px` }
    );

    io.observe(element);
  } else {
    callback();
  }
}

function getFocusableElements(container) {
  return Array.from(container.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"));
}

document.querySelectorAll('[id^="Details-"]:not(.menu-drawer__accordion) > summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
  });

  if (summary.closest("header-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (elementToFocus.tagName === "INPUT" && ["search", "text", "email", "url"].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ["ARROWUP", "ARROWDOWN", "ARROWLEFT", "ARROWRIGHT", "TAB", "ENTER", "SPACE", "ESCAPE", "HOME", "END", "PAGEUP", "PAGEDOWN"];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*");
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onButtonClick.bind(this)));
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle("disabled", value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options["hideElement"] || province_domid);

  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) => summary.addEventListener("click", this.onSummaryClick.bind(this)));
    this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary")) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;
    this.closeAnimation(this.mainDetailsToggle);
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.querySelector("#menu-drawer").classList.remove("opened-menu");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset = this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0;
    document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
      this.querySelector("#menu-drawer").classList.add("opened-menu");
    });

    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
  }
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener("click", this.hide.bind(this, false));
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(content.querySelector("video, model-viewer, iframe"));
      if (focus) deferredElement.focus();
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    //Jolly Commerce-------------------------------------------------------------------
    //Code in this block is responsible for pagination on product slider(jolly-main-product)
    if (this.pageTotalElement) {
      let pagination = '<div class="product-pagination-wrapper">';

      for (let i = 0; i < parseInt(this.pageTotalElement.innerHTML); i++) {
        pagination += '<div class="product-pagination"></div>';
      }
      pagination += "</div>";
      this.slider.insertAdjacentHTML("afterend", pagination);
      this.pagination = this.slider.nextSibling.querySelectorAll(".product-pagination");
    }
    //Jolly Commerce-------------------------------------------------------------------

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      //Jolly Commerce-------------------------------------------------------------------
      //Code in this block is responsible for pagination on product slider(jolly-main-product)
      if (this.pagination) {
        this.pagination.forEach((el, i) => {
          if (i == this.currentPage - 1) {
            el.classList.add("product-pagination-active");
          } else {
            el.classList.remove("product-pagination-active");
          }
        });
      }
      //Jolly Commerce-------------------------------------------------------------------
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === "next" ? this.slider.scrollLeft + step * this.sliderItemOffset : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add("slider-counter__link--active");
    this.sliderControlButtons[this.currentPage - 1].setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
    this.play();
  }

  focusInHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.slider.querySelector(".slideshow__slide").clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

// Only define slideshow-component if it hasn't been defined yet (by slideshow.js)
if (!customElements.get("slideshow-component")) {
  customElements.define("slideshow-component", SlideshowComponent);
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.previewVariant = null;
  }

  connectedCallback() {
    this.listenEvents();
    this.variantInit();
  }

  listenEvents() {
    this.addEventListener("updateInput", (event) => {
      this.onVariantChange(event);
    });
    this.addEventListener("change", (event) => {
      this.onVariantChange(event);
      //Jolly Commerce------------------------------------------------------------
      //2 variant
      // if (e.target.hasAttribute('jolly-data-type')) {
      //   setCookie("Customer_product_size", e.target.value, 15);
      // }
      //Jolly Commerce------------------------------------------------------------
    });
    if (this.closest(".product-bundle-set") || this.closest(".main-jolly-product-set")) {
      this.addEventListener("click", (event) => {
        const label = event.target.closest(".variant-colorswatch-bundle-set-container .tr-label-color-wrap label");
        if (!label || !label.htmlFor) return;
        const radio = this.querySelector(`#${CSS.escape(label.htmlFor)}`);
        if (radio && radio.type === "radio" && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }, true);
    }
  }

  // updateCookies(e) {
  //   let currentCookie = getCookie("Selected_variant")
  //   let currentVal = e.target.getAttribute('id');
  //   currentCookie = currentCookie.split(",");
  //   for (let i = 0; i < currentCookie.length; i++) {
  //     const element = currentCookie[i].substring(0, currentCookie[i].length - 2);
  //     const elementToAdd = currentVal.substring(0, currentVal.length - 2);
  //     if (element == elementToAdd) {
  //       currentCookie.splice(i, 1)
  //     }
  //   }
  //   if (currentCookie == "") {
  //     currentCookie += e.target.getAttribute('id')
  //   }
  //   else {
  //     currentCookie += "," + e.target.getAttribute('id')
  //   }
  //   setCookie("Selected_variant", currentCookie, 15)
  // }

  variantInit() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, "", false);
    this.removeErrorMessage();
    this.updateVariantStatuses();
    this.updateVariantFieldname();

    if (!this.currentVariant) {
      this.toggleAddButton(true, "", true);
      this.setUnavailable();
    } else {
      this.updateURL();
      this.updateVariantInput();
      this.showPreorderOrDelivery();
      this.renderProductInfo();
      this.updateShareUrl();
      if (this.closest(".product-bundle-set") || this.closest(".main-jolly-product-set")) {
        this.toggleAddButton(false, "", true);
        this.syncBundleSetColorSelectedState();
      }
    }
  }

  onVariantChange(event) {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, "", false);
    //this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses(event);
    this.updateVariantFieldname();

    if (!this.currentVariant) {
      this.toggleAddButton(true, "", true);
      this.setUnavailable();
      this.updateMedia(true);
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.showPreorderOrDelivery();
      //document.querySelector("deployer-quantity").classList.add("loading");
      this.renderProductInfo();
      this.updateShareUrl();
      if (this.closest(".product-bundle-set") || this.closest(".main-jolly-product-set")) {
        this.toggleAddButton(false, "", true);
        this.syncBundleSetColorSelectedState();
      }
    }
  }

  updateVariantFieldname() {
    document.querySelectorAll(".variant_fieldset").forEach((variantFieldset) => {
      variantFieldset.querySelectorAll(".variant_group").forEach((variantGroup) => {
        variantGroup.querySelectorAll("input").forEach((input) => {
          if (input.checked) {
            variantFieldset.querySelector(".color-variant-picker-title").innerHTML = input.value.split("|")[0];
          }
        });
      });
    });
  }

  /**
   * Synchronise l’état visuel "selected" des couleurs dans le bundle set
   * (variants-swiper-color) pour que le bon label ait la bordure sélectionnée.
   */
  syncBundleSetColorSelectedState() {
    const colorFieldset = this.querySelector(".variant-colorswatch-bundle-set-container");
    if (!colorFieldset) return;
    const wrap = colorFieldset.querySelectorAll(".tr-label-color-wrap");
    wrap.forEach((el) => {
      const label = el.querySelector("label");
      if (label) label.classList.remove("selected");
    });
    const checked = colorFieldset.querySelector('input[type="radio"]:checked');
    if (checked) {
      const next = checked.nextElementSibling;
      if (next && next.classList.contains("tr-label-color-wrap")) {
        const label = next.querySelector("label");
        if (label) label.classList.add("selected");
      }
    }
  }

  /* updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  } */

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
    if (!this.currentVariant) {
      this.previewVariant = this.getVariantData().find((variant) => {
        return variant.options[0] == this.options[0];
      });
    }
  }

  // Used to update product card image
  updateMediaProductCard(html) {
    const srcImage = html.querySelector("slider-component img").getAttribute("src");
    const srcsetImage = html.querySelector("slider-component img").getAttribute("srcset");
    const productId = html.querySelector("[data-product-id]").getAttribute("data-product-id");
    const jollyMedia = document.querySelector(`[data-jolly-media=MediaGallery-${this.dataset.section}]`);

    if (jollyMedia != null) {
      jollyMedia.querySelector("img").setAttribute("srcset", srcsetImage);
      jollyMedia.querySelector("img").setAttribute("src", srcImage);
    }
  }

  updateMedia(withoutSize) {
    const selectedVariant = withoutSize ? this.previewVariant : this.currentVariant;
    if (!selectedVariant) return;
    if (!selectedVariant.featured_media) return;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);

    if (!mediaGalleries) {
      // mediaGalleries = document.querySelectorAll('');
      // this.updateMediaProductCard();
      return;
    }
    if (mediaGalleries.length >= 1) {
      mediaGalleries.forEach((mediaGallery) => mediaGallery.setActiveMedia(`${this.dataset.section}-${selectedVariant.featured_media.id}`, true));
    } else {
    }

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${selectedVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;
    window.history.replaceState({}, "", `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, form[data-jolly-id*="${this.dataset.productid}"]`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));

      const stocks = this.getStocks();
      if (stocks) {
        const stockInput = productForm.querySelector('input[name="stock"]');
        stockInput.value = stocks[this.currentVariant.id];
        stockInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  updateVariantStatuses(event) {
    if (!this.querySelector(":checked")) return;

    if (this.variantData == null) return;

    if (this.dataset.pack == "false") {
      this.setSizeInputAvailability();
    }
    const selectedOptionOneVariants = this.variantData.filter((variant) => {
      return this.querySelector(":checked").value == variant.option1;
    });

    const inputWrappers = [...this.querySelectorAll(".product-form__input")];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(":checked").value;
      const availableOptionInputsValue = selectedOptionOneVariants.filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected).map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);

      if (event) {
        if (event.srcElement.closest(".product-bundle-set")) return;
        if (event.srcElement.closest("cart-drawer")) return;
        if (event.srcElement.closest(".main-jolly-product-set")) return;
        if (!window.location.href.includes("/products/")) return;
      } else {
        this.selectFirstAvailableOption(optionInputs, availableOptionInputsValue);
      }

      // if(
      // !event.srcElement.closest('fieldset').classList.contains('variant-offre-container') &&
      // !event.srcElement.closest('fieldset').classList.contains('variant-size-container') &&
      // !event.srcElement.closest('fieldset').classList.contains('variant-colorswatch-bundle-set-container')
      // ){
      //   this.selectFirstAvailableOption(optionInputs, availableOptionInputsValue);
      // }
    });
  }

  setSizeInputAvailability() {
    const inputWrappers = [...this.querySelectorAll(".product-form__input")];
    const selectedValues = inputWrappers.map((option, index) => {
      return this.options[index];
    });

    inputWrappers.forEach((wrapper) => {
      const radioInputs = wrapper.querySelectorAll('input[type="radio"]');

      radioInputs.forEach((input) => {
        const value = input.value;
        let isAvailable = false;

        this.variantData.forEach((variant) => {
          // Vérifie si la variante est disponible
          // Si il n'y a qu'une seule option, on ne vérifie que variant.option1
          // Sinon on vérifie variant.option1 et variant.option2
          if (variant.available && variant.option1 === value) {
            if (selectedValues[1] === undefined || variant.option2 === selectedValues[1]) {
              isAvailable = true;
            }
          }
        });

        if (isAvailable) {
          input.classList.remove("disabled");
        } else {
          input.classList.add("disabled");
        }
      });
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute("value"))) {
        input.innerText = input.getAttribute("value");
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace("[value]", input.getAttribute("value"));
      }
    });
  }

  selectFirstAvailableOption(listOfOptions, listOfAvailableOptions) {
    for (const optionInput of listOfOptions) {
      if (listOfAvailableOptions.includes(optionInput.value) && !optionInput.classList.contains("disabled")) {
        optionInput.click();
        break;
      }
    }
    return;
  }

  // updatePickupAvailability() {
  //   const pickUpAvailability = document.querySelector('pickup-availability');

  //   if (!pickUpAvailability) return;

  //   if (this.currentVariant && this.currentVariant.available) {
  //     pickUpAvailability.fetchAvailability(this.currentVariant.id);
  //   } else {
  //     pickUpAvailability.removeAttribute('available');
  //     pickUpAvailability.innerHTML = '';
  //   }
  // }

  // updatePickupAvailability() {
  //   const pickUpAvailability = document.querySelector('pickup-availability');
  //   if (!pickUpAvailability) return;

  //   if (this.currentVariant && this.currentVariant.available) {
  //     pickUpAvailability.fetchAvailability(this.currentVariant.id);
  //   } else {
  //     pickUpAvailability.removeAttribute('available');
  //     pickUpAvailability.innerHTML = '';
  //   }
  // }

  removeErrorMessage() {
    // const section = this.closest('section');
    // replace with closest element that contain class
    const section = this.closest("[class*=section-template]");
    if (!section) return;

    const productForm = section.querySelector("product-form");
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo(forDefault) {
    if (this.closest(".product-bundle-set") || this.closest(".main-jolly-product-set")) return;

    const requestedVariant = forDefault ? this.previewVariant : this.currentVariant;
    const requestedVariantId = forDefault ? this.previewVariant.id : this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    // fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`)
    // update for more sorf logic
    // we want to be able to fetch without product section id!!!
    fetch(`${this.dataset.url}?variant=${requestedVariantId}`)
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant && this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, "text/html");
        const destination = document.getElementById(`price-${this.dataset.section}`);
        // const source = html.getElementById(`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        //more sor confitions on price elements
        const source = html.querySelector(`[id*=price-]`);
        const skuSource = html.getElementById(`Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(`Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        const preOrder = html.getElementById(`preorder-${this.dataset.section}`);
        const form = document.getElementById(`product-form-${this.dataset.section}`);

        // Gestion des inputs _preOrder - respecter la logique du template
        if (form) {
          // Supprimer d'abord tous les anciens inputs _preOrder
          const existingPreorderInputs = form.querySelectorAll('input[name="properties[_preOrder]"]');
          existingPreorderInputs.forEach((input) => {
            input.remove();
          });

          // Ajouter le nouvel input seulement si preOrder a du contenu (même logique que le template)
          if (preOrder && preOrder.innerHTML.trim() !== "") {
            const preorderInput = document.createElement("input");
            preorderInput.type = "hidden";
            preorderInput.name = "properties[_preOrder]";
            preorderInput.value = preOrder.innerHTML.trim();
            form.appendChild(preorderInput);
          }
        }

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle("visibility-hidden", skuSource.classList.contains("visibility-hidden"));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove("tr-hidden");

        if (inventoryDestination) inventoryDestination.classList.toggle("visibility-hidden", inventorySource.innerText === "");

        // Utiliser uniquement le bouton de cette section pour éviter qu'un autre (ex. barre sticky "Choisir taille") force le bouton principal en désactivé
        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        //Jolly Commerce-------------------------------------------------------------------
        //Code in this block is responsible for product variants(jolly-variant-options)
        const colorVariant = html.querySelectorAll(`#Variant-picker-image-switch-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const colorImage = document.querySelector('div[id^="variant_change_CHOISISSEZ_VOTRE_COULEUR"]');
        const sizeImage = document.querySelector('div[id^="variant_change_CHOISISSEZ_VOTRE_TAILLE"]');
        const sizeText = document.querySelector('div[id^="variant_change_CHOIX_DE_LA_TAILLE"]');
        const sizeText2 = document.querySelector('div[id^="variant_change_TAILLE"]');
        const colorTitle = document.querySelector('span[id^="Color-variant-picker-title-CHOISISSEZ_VOTRE_COULEUR"]');
        const sizeTitle = document.querySelector('span[id^="Color-variant-picker-title-CHOISISSEZ_VOTRE_TAILLE"]');
        const sizeTitleLetter = document.querySelector('span[id^="Color-variant-picker-title-CHOIX_DE_LA_TAILLE"]');
        const sizeTitleLetter2 = document.querySelector('span[id^="Color-variant-picker-title-TAILLE"]');
        const sizeTitlePrice = document.querySelector('span[id^="Color-variant-picker-title-Montant"]');
        const atcColor = document.querySelector(".tr-atc-color");
        const atcColorName = document.querySelector(".tr-atc-color-name");
        const variantsOffre = document.querySelector(".js-variant-offre");
        const klaviyoContainer = document.querySelector(".klaviyo-bis-trigger");

        if (urlParams.get("variant") == requestedVariantId) {
          if (variantsOffre && this.classList.contains("tr-product-variant")) {
            variantsOffre.innerHTML = html.querySelector(".js-variant-offre").innerHTML;
          }

          var deployerQuantity = document.querySelector("deployer-quantity");

          console.log(deployerQuantity);

          if (deployerQuantity && typeof deployerQuantity.getCurrentVariantType === "function" && deployerQuantity.getCurrentVariantType() == "mix") {
            deployerQuantity.updateAvailableQuantityOffers();
          } else {
            if (!requestedVariant.available) {
              if (document.querySelector(".js-atc-wrapper")) {
                document.querySelector(".js-atc-wrapper").classList.add("js-atc-wrapper-hide-delivery");
              }
              if (klaviyoContainer) {
                if (klaviyoContainer.closest("quick-add-modal") == null) klaviyoContainer.classList.remove("tr-hidden");
              }
            } else {
              if (klaviyoContainer) {
                klaviyoContainer.classList.add("tr-hidden");
              }
              if (document.querySelector(".js-atc-wrapper")) {
                document.querySelector(".js-atc-wrapper").classList.remove("js-atc-wrapper-hide-delivery");
              }
            }
          }
        }

        for (let i = 0; i < colorVariant.length; i++) {
          if (
            colorVariant[i]
              .querySelector("input[checked]")
              ?.getAttribute("name")
              .match(new RegExp(/VOTRE.*COULEUR/))?.input
          ) {
            colorImage.style.backgroundImage = colorVariant[i].querySelector("input[checked]").nextElementSibling.querySelector(".js-variable-color").style.backgroundImage;
            atcColor.style.backgroundImage = colorVariant[i].querySelector("input[checked]").nextElementSibling.querySelector(".js-variable-color").style.backgroundImage;
            colorTitle.innerHTML = colorVariant[i].querySelector("input[checked]").getAttribute("value").split(" •")[0];
            atcColorName.innerHTML = colorVariant[i].querySelector("input[checked]").getAttribute("value").split(" •")[0];
            this.updateMediaProductCard(html);
          } else if (
            colorVariant[i]
              .querySelector("input[checked]")
              ?.getAttribute("name")
              .match(new RegExp(/VOTRE.*OFFRE/))?.input
          ) {
            continue;
          } else if (
            colorVariant[i]
              .querySelector("input[jolly-data-size-checked]")
              ?.getAttribute("name")
              .match(new RegExp(/VOTRE.*TAILLE/))?.input
          ) {
            sizeImage.innerHTML = colorVariant[i].querySelector("input[jolly-data-size-checked]").nextElementSibling.innerHTML;
            sizeTitle.innerHTML = colorVariant[i].querySelector("input[jolly-data-size-checked]").getAttribute("value");
          } else if (
            colorVariant[i]
              .querySelector("input[jolly-data-size-checked]")
              ?.getAttribute("name")
              .match(new RegExp(/CHOIX.*DE.*LA.*TAILLE/))?.input
          ) {
            sizeText.innerHTML = colorVariant[i].querySelector("input[jolly-data-size-checked]").nextElementSibling.innerHTML;
            sizeTitleLetter.innerHTML = colorVariant[i].querySelector("input[jolly-data-size-checked]").getAttribute("value");
          } else if (
            colorVariant[i]
              .querySelector("input[jolly-data-size-checked]")
              ?.getAttribute("name")
              .match(new RegExp(/TAILLE/))?.input
          ) {
            sizeText2.innerHTML = colorVariant[i].querySelector("input[jolly-data-size-checked]").nextElementSibling.innerHTML;
            sizeTitleLetter2.innerHTML = colorVariant[i].querySelector("input[jolly-data-size-checked]").getAttribute("value");
          } else if (
            colorVariant[i]
              .querySelector("input[checked]")
              ?.getAttribute("name")
              .match(new RegExp(/Montant/))?.input
          ) {
            sizeTitlePrice.innerHTML = colorVariant[i].querySelector("input[checked]").getAttribute("value");
          }
        }

        if (this.closest(".main-jolly-product-set") || forDefault) {
          this.updateMediaProductCard(html);
        }

        // jollyMedia.querySelector('img').setAttribute('srcset', srcsetImage);
        // jollyMedia.querySelector('img').setAttribute('src', srcImage);

        //   selectedProductImage.setAttribute('data-src', firstImage);
        //Jolly Commerce-------------------------------------------------------------------

        this.toggleAddButton(addButtonUpdated ? addButtonUpdated.hasAttribute("disabled") : true);

        // For products without Klaviyo back-in-stock button (tagged 'limited-edition'),
        // show the sold-out label on the ATC button instead of the loading spinner
        if (!klaviyoContainer && !requestedVariant.available && addButtonUpdated) {
          const targetButton =
            document.getElementById(`ProductSubmitButton-${sectionId}`) ||
            document.querySelector('product-form button[name="add"]') ||
            document.querySelector(".buy-buttons__submit");
          const updatedText = addButtonUpdated.querySelector("span")?.textContent?.trim();
          if (targetButton && updatedText) {
            const buttonSpan = targetButton.querySelector("span");
            if (buttonSpan) buttonSpan.textContent = updatedText;
            targetButton.classList.remove("loading");
            const spinner = targetButton.querySelector(".loading-overlay__spinner");
            if (spinner) spinner.classList.add("hidden");
          }
        }

        var isMainProductPage = false;
        if (urlParams.get("variant") == requestedVariantId) isMainProductPage = true;

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            mainProductPage: isMainProductPage,
            html,
            variant: requestedVariant,
          },
        });
      })
      .catch((err) => {
        console.error("renderProductInfo fetch error", err);
        this.toggleAddButton(false, "", true);
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    let productForm = document.getElementById(`product-form-${this.dataset.section}`);
    const delivery_text = document.querySelector(".tr-delivery__on-product");

    if (!productForm) {
      productForm = document.querySelector(`form[data-jolly-id="${this.dataset.section}"]`);
    }

    if (!productForm) return;

    const idInput = productForm.querySelector('[name="id"]');
    let productFormId = idInput ? idInput.value : null;
    const addButton = productForm.querySelector('[name="add"]');
    addButton.setAttribute("type", "submit");
    const addButtonText = productForm.querySelector('[name="add"] > span');

    if (!addButton) return;

    if (disable) {
      addButton.setAttribute("disabled", "disabled");

      if (addButton.closest("quick-add-modal") == null) {
        addButton.classList.add("loading");
        addButton.querySelector(".loading-overlay__spinner").classList.remove("hidden");
      }

      if (delivery_text) {
        // delivery_text.style.opacity = '0';
      }

      if (text) addButtonText.textContent = text;
      if (addButton.dataset.upsell) addButtonText.textContent = addButton.dataset.upsell;

      if (this.closest("product-info") != null) {
        if (this.closest("product-info").querySelector('span[id^="price-quickadd-"]') && this.currentVariant) {
          this.closest("product-info").querySelector('span[id^="price-quickadd-"]').innerHTML = `<span> • </span> ${this.formatMoney(this.currentVariant.price)}`;
        }
      } else {
        if (this.closest(".product-info").querySelector('span[id^="price-quickadd-"]') && this.currentVariant) {
          this.closest(".product-info").querySelector('span[id^="price-quickadd-"]').innerHTML = `<span> • </span> ${this.formatMoney(this.currentVariant.price)}`;
        }
      }
    } else {
      //Debug du bouton avec l'id : ProductSubmitButton-template--24591455289680__262c3d88-cd50-4aa0-b501-fe9071d82e3c
      if ((addButton.id = "ProductSubmitButton-template--24591455289680__262c3d88-cd50-4aa0-b501-fe9071d82e3c")) {
        //attend 20 secondes
        setTimeout(() => {
          addButton.classList.remove("loading");
        }, 1000);
      }

      addButton.removeAttribute("disabled");
      addButton.classList.remove("loading");
      addButton.querySelector(".loading-overlay__spinner").classList.add("hidden");

      if (delivery_text) {
        delivery_text.style.opacity = "1";
      }

      // Vérifier si la variante actuelle a une date de précommande
      const template = this.querySelector("template");
      let isPreorder = false;

      if (template) {
        const options = template.content.querySelectorAll("option");
        options.forEach((option) => {
          if (option.value == this.currentVariant.id && option.dataset.preorderDate != undefined) {
            isPreorder = true;
          }
        });
      }

      if (isPreorder) {
        addButtonText.textContent = window.variantStrings.addToCart;
      } else {
        addButtonText.textContent = window.variantStrings.addToCart;
        if (addButton.dataset.upsell) addButtonText.textContent = addButton.dataset.upsell;
        if (this.closest("product-info") != null) {
          if (this.closest("product-info").querySelector('span[id^="price-quickadd-"]')) {
            this.closest("product-info").querySelector('span[id^="price-quickadd-"]').innerHTML = `<span> • </span> ${this.formatMoney(this.currentVariant.price)}`;
          }
        } else {
          if (this.closest(".product-info").querySelector('span[id^="price-quickadd-"]')) {
            this.closest(".product-info").querySelector('span[id^="price-quickadd-"]').innerHTML = `<span> • </span> ${this.formatMoney(this.currentVariant.price)}`;
          }
        }
      }
    }

    if (!modifyClass) return;
  }

  formatMoney(price) {
    return `${window.accessibilityStrings.moneyFormat.replace("{{amount_with_comma_separator}}", "")} ${parseInt(price / 100).toFixed(2)}`;
  }

  setUnavailable() {
    let button = document.getElementById(`product-form-${this.dataset.section}`);

    if (!button) {
      button = document.querySelector(`form[data-jolly-id="${this.dataset.section}"]`);
    }

    if (!button) return;

    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.selectSize;
    addButton.setAttribute("type", "button");
    addButton.removeAttribute("disabled");

    if (price) price.classList.add("tr-hidden");
    if (inventory) inventory.classList.add("visibility-hidden");
    if (sku) sku.classList.add("visibility-hidden");
  }

  showPreorderOrDelivery() {
    if (!this.querySelector("template")) return;

    const options = this.querySelector("template").content.querySelectorAll("option");
    const closestProductInfo = this.closest("product-info");
    if (!closestProductInfo) return;

    const deliveryEstimation = closestProductInfo.querySelector(".js-delivery-estimation .tr-delivery-fill-please");
    const preorderText = closestProductInfo.querySelector(".js-delivery-estimation .tr-delivery-preorder");
    if (!deliveryEstimation) return;

    const stickyDeliveryEstimation = closestProductInfo.querySelector(".js-bar .tr-delivery-fill-please");
    const stickyPreorderText = closestProductInfo.querySelector(".js-bar .tr-delivery-preorder");

    if (this.currentVariant.available) {
      let showEstimation = false;

      options.forEach((option) => {
        if (option.value == this.currentVariant.id) {
          if (option.dataset.preorderDate != undefined) {
            showEstimation = true;
            deliveryEstimation.classList.add("tr-hidden");
            preorderText.classList.remove("tr-hidden");
            if (stickyDeliveryEstimation != null) {
              stickyDeliveryEstimation.classList.add("tr-hidden");
              if (stickyPreorderText != null) {
                stickyPreorderText.classList.remove("tr-hidden");
              }
            }

            // Formater la date pour l'affichage
            const preorderDate = new Date(option.dataset.preorderDate);
            const formattedDate = preorderDate.toLocaleDateString("fr-FR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            });

            const preorderMessage = `Demande très forte, livraison à partir du ${formattedDate}`;
            preorderText.innerText = preorderMessage;
            if (stickyPreorderText != null) {
              stickyPreorderText.innerText = preorderMessage;
            }
          } else {
            deliveryEstimation.classList.remove("tr-hidden");
            preorderText.classList.add("tr-hidden");
            if (stickyDeliveryEstimation != null) {
              stickyDeliveryEstimation.classList.remove("tr-hidden");
              if (stickyPreorderText != null) {
                stickyPreorderText.classList.add("tr-hidden");
              }
            }
          }
        }
      });

      // Vérifier si deliveryEstimation et preorderText ont du contenu
      const hasDeliveryContent = deliveryEstimation && !deliveryEstimation.classList.contains("tr-hidden") && deliveryEstimation.innerText.trim() !== "";
      const hasPreorderContent = preorderText && !preorderText.classList.contains("tr-hidden") && preorderText.innerText.trim() !== "";

      if (showEstimation || hasDeliveryContent || hasPreorderContent) {
        document.querySelector(".js-delivery-estimation").classList.remove("tr-hidden");
      } else {
        document.querySelector(".js-delivery-estimation").classList.add("tr-hidden");
      }
    } else {
      document.querySelector(".js-delivery-estimation").classList.add("tr-hidden");
    }
  }

  getStocks() {
    if (!this.querySelector(".qties")) {
      return null;
    }
    return JSON.parse(this.querySelector(".qties").textContent);
  }
  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define("variant-selects", VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute("value"))) {
        input.classList.remove("disabled");
        //input.removeAttribute('disabled');
      } else {
        input.classList.add("disabled");
        //input.checked = false;
        //input.setAttribute('disabled', '');
      }
    });
  }

  updateOptions() {
    const fieldsets = this.querySelectorAll("fieldset");

    let result = [];

    fieldsets.forEach(function (fieldset) {
      let radios = fieldset.querySelectorAll('input[type="radio"]:checked');

      radios.forEach(function (radio) {
        result.push(radio.value);
      });
    });

    this.options = result;

    /*
     * Old code doesn't works
     */
    // this.options = fieldsets.map((fieldset) => {
    //   return Array.from(fieldset.querySelectorAll('input')).find((radio) => {
    //      radio.checked || radio.hasAttribute("data-checked")
    //     }).value;
    // });
  }
}

customElements.define("variant-radios", VariantRadios);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("product-recommendations");

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
            this.remove();
          }

          if (html.querySelector(".grid__item")) {
            this.classList.add("product-recommendations--loaded");
          }
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 400px 0px",
    }).observe(this);
  }
}
customElements.define("product-recommendations", ProductRecommendations);

class UpsellAddToCartButton extends HTMLElement {
  constructor() {
    super();

    this.productId = this.getAttribute("data-product-id");
    this.variantId = this.getAttribute("data-variant-id");
    this.quantity = this.getAttribute("data-quantity");

    this.submitButton = this.querySelector('[type="submit"]');

    this.cart = document.querySelector(".drawer");
  }

  connectedCallback() {
    this.submitButton.addEventListener("click", () => {
      this.addToCart();
    });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper");
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector(".product-form__error-message");

    this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  async addToCart() {
    console.log("add to cart");

    if (this.submitButton.getAttribute("aria-disabled") === "true") return;

    this.handleErrorMessage();

    this.submitButton.setAttribute("aria-disabled", true);
    this.submitButton.classList.add("loading");

    const config = fetchConfig("javascript");
    config.method = "POST";
    config.headers["Content-Type"] = "application/json";

    config.body = JSON.stringify({
      items: [
        {
          id: this.variantId,
          quantity: this.quantity,
        },
      ],
      sections: this.cart.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    await fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        this.cart.renderContents(response);
        window.dispatchEvent(this.openCart);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (this.cart && this.cart.classList.contains("is-empty")) this.cart.classList.remove("is-empty");
        this.submitButton.classList.remove("loading");
        this.submitButton.setAttribute("aria-disabled", false);
      });
  }
}

customElements.define("upsell-add-to-cart-button", UpsellAddToCartButton);

//Jolly Commerce------------------------------------------------------------
function setCookie(name, value, days) {
  var expires = "";
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + value + expires + "; path=/";
}

function getCookie(name) {
  var cookieArray = document.cookie.split("; ");
  for (var i = 0; i < cookieArray.length; i++) {
    var cookie = cookieArray[i];
    var cookieParts = cookie.split("=");
    var cookieName = cookieParts[0];
    var cookieValue = cookieParts[1];
    if (cookieName === name) {
      return cookieValue;
    }
  }
  return "";
}

// window.addEventListener("load", updateFromCookie, false);

// function updateFromCookie() {
//   let eventUpdateInput1 = new Event('updateInput', {
//     bubbles: true
//   })
//   let currentCookie = getCookie("Selected_variant");
//   currentCookie = currentCookie.split(",");
//   let id_from_cookie = []
//   const product_block = document.querySelector("[data-product-id]");
//   document.querySelectorAll("variant-radios").forEach((el) => {
//     if (el.id.split("-")[2] != "template") {
//       id_from_cookie.push(el.id.split("-")[2])
//     }
//   })

//   if (id_from_cookie.length != 0) {
//     for (let i = 0; i < currentCookie.length; i++) {
//       let element = currentCookie[i].substring(0, currentCookie[i].length - 4);
//       for (let j = 0; j < id_from_cookie.length; j++) {
//         if (element == id_from_cookie[j]) {
//           // document.querySelector("label[for='" + currentCookie[i] + "']").dispatchEvent(eventUpdateInput1)
//           document.querySelector("label[for='" + currentCookie[i] + "']")?.click()
//         }
//       }
//     }
//   }

//   if (product_block) {
//     let product_id = product_block.getAttribute("data-product-id");
//     for (let i = 0; i < currentCookie.length; i++) {
//       let element = currentCookie[i].substring(0, currentCookie[i].length - 4);
//       if (element == product_id) {
//         // document.querySelector("label[for='" + currentCookie[i] + "']").dispatchEvent(eventUpdateInput1)
//         document.querySelector("label[for='" + currentCookie[i] + "']")?.click()
//       }
//     }
//   }
// }

// mobile menu accordion
class Accordion {
  constructor(el) {
    this.el = el;
    this.summary = el.querySelector("summary");
    this.content = el.querySelector(".menu-drawer__submenu");

    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.summary.addEventListener("click", (e) => this.onClick(e));
  }

  onClick(e) {
    e.preventDefault();
    this.el.style.overflow = "hidden";
    if (this.isClosing || !this.el.open) {
      this.open();
    } else if (this.isExpanding || this.el.open) {
      this.shrink();
    }
  }

  shrink() {
    this.isClosing = true;

    const startHeight = `${this.el.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.el.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 400,
        easing: "ease-out",
      }
    );

    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => (this.isClosing = false);
  }

  open() {
    this.el.style.height = `${this.el.offsetHeight}px`;
    this.el.open = true;
    window.requestAnimationFrame(() => this.expand());
  }

  expand() {
    this.isExpanding = true;
    const startHeight = `${this.el.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.el.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 400,
        easing: "ease-out",
      }
    );
    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => (this.isExpanding = false);
  }

  onAnimationFinish(open) {
    this.el.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.el.style.height = this.el.style.overflow = "";
  }
}

document.querySelectorAll(".menu-drawer__accordion").forEach((el) => {
  new Accordion(el);
});

//2 variant
// if (document.querySelector('[jolly-data-type]')) {
//   const sizeVariants = document.querySelectorAll('[jolly-data-type]')
//   const sizeCookieName = getCookie("Customer_product_size")

//   sizeVariants.forEach(size => {
//     if (size.value === sizeCookieName) {
//       size.checked = true;
//     }
//   })
// }

if (document.querySelector("[jolly-data-money]")) {
  document.getElementById("Color-variant-picker-title-Montant").innerHTML = document.querySelector("[jolly-data-money]:checked").value;
}

if (!customElements.get("sticky-header")) {
  customElements.define(
    "sticky-header",
    class StickyHeader extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.header = document.querySelector(".section-header");
        this.headerIsAlwaysSticky = this.getAttribute("data-sticky-type") === "always" || this.getAttribute("data-sticky-type") === "reduce-logo-size";
        this.headerBounds = {};

        this.setHeaderHeight();

        window.matchMedia("(max-width: 990px)").addEventListener("change", this.setHeaderHeight.bind(this));

        if (this.headerIsAlwaysSticky) {
          this.header.classList.add("shopify-section-header-sticky");
        }

        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.predictiveSearch = this.querySelector("predictive-search");

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.addEventListener("scroll", this.onScrollHandler, false);

        this.createObserver();
      }

      setHeaderHeight() {
        document.documentElement.style.setProperty("--header-height", `${this.header.offsetHeight}px`);
      }

      disconnectedCallback() {
        this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.removeEventListener("scroll", this.onScrollHandler);
      }

      createObserver() {
        let observer = new IntersectionObserver((entries, observer) => {
          this.headerBounds = entries[0].intersectionRect;
          observer.disconnect();
        });

        observer.observe(this.header);
      }

      onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add("scrolled-past-header");
          if (this.preventHide) return;
          requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add("scrolled-past-header");
          if (!this.preventReveal) {
            requestAnimationFrame(this.reveal.bind(this));
          } else {
            window.clearTimeout(this.isScrolling);

            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 66);

            requestAnimationFrame(this.hide.bind(this));
          }
        } else if (scrollTop <= this.headerBounds.top) {
          this.header.classList.remove("scrolled-past-header");
          requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
      }

      hide() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add("shopify-section-header-hidden", "shopify-section-header-sticky");
        this.closeMenuDisclosure();
        this.closeSearchModal();
      }

      reveal() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add("shopify-section-header-sticky", "animate");
        this.header.classList.remove("shopify-section-header-hidden");
      }

      reset() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.remove("shopify-section-header-hidden", "shopify-section-header-sticky", "animate");
      }

      closeMenuDisclosure() {
        this.disclosures = this.disclosures || this.header.querySelectorAll("header-menu");
        //this.disclosures.forEach(disclosure => disclosure.close());
      }

      closeSearchModal() {
        this.searchModal = this.searchModal || this.header.querySelector("details-modal");
        this.searchModal.close(false);
      }
    }
  );
}

// Countdown Timer
class CountdownTimerDrawer extends HTMLElement {
  constructor() {
    super();

    this.countdownTimerNumber = this.querySelector(".countdown-timer__number");
  }

  connectedCallback() {
    this.initializeCountDownDrawer();
  }

  loadCountDownDrawer() {
    const now = Math.floor(Date.now() / 1000); // Temps actuel en secondes
    let countdownEndTime;

    // Si un cookie existe, récupère l'heure de fin
    if (getCookie("countdown-timer")) {
      //si le cookie existe et que le temps est supérieur à 0
      countdownEndTime = parseInt(getCookie("countdown-timer"), 10);
      if (countdownEndTime <= now) {
        // Vérification si le temps est écoulé
        countdownEndTime = now + 1800; // Réinitialisation à 30 minutes
        setCookie("countdown-timer", countdownEndTime, 1); // Stocke l'heure de fin dans un cookie
      }
    } else {
      countdownEndTime = now + 1800;
      setCookie("countdown-timer", countdownEndTime, 1); // Stocke l'heure de fin dans un cookie
    }

    const interval = setInterval(() => {
      const currentTime = Math.floor(Date.now() / 1000); // Temps actuel
      const remainingTime = countdownEndTime - currentTime;

      if (remainingTime <= 0) {
        clearInterval(interval);
        setCookie("countdown-timer", "", -1);

        countdownEndTime = now + 1800;
        setCookie("countdown-timer", countdownEndTime, 1);
      }

      const minutes = Math.floor(remainingTime / 60);
      const seconds = remainingTime % 60;

      // Formatage des secondes pour avoir toujours deux chiffres
      const formattedSeconds = String(seconds).padStart(2, "0");

      if (this.countdownTimerNumber) {
        this.countdownTimerNumber.textContent = `${minutes}:${formattedSeconds}`;
      }
    }, 1000);
  }

  initializeCountDownDrawer() {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadCountDownDrawer();
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  getCookie(name) {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const [key, value] = cookie.trim().split("=");
      if (key === name) {
        return value;
      }
    }
    return null;
  }

  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
  }
}

customElements.define("countdown-timer-drawer", CountdownTimerDrawer);

class PochetteCadeau extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector("input");
    this.cart = this.findCartElement();
  }

  findCartElement() {
    // Essayer de trouver l'élément cart avec plusieurs stratégies
    let cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");

    // Si pas trouvé, attendre un peu et réessayer (pour les cas où le DOM n'est pas encore complètement chargé)
    if (!cart) {
      setTimeout(() => {
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      }, 100);
    }

    return cart;
  }

  connectedCallback() {
    console.log(this.input);
    this.input.addEventListener("change", this.onClick.bind(this));
  }

  onClick() {
    if (this.input.checked) {
      this.addProductToCart();
    } else {
      this.removeProductFromCart();
    }
  }

  addProductToCart() {
    // Vérifier si this.cart existe avant de continuer, sinon essayer de le retrouver
    if (!this.cart) {
      this.cart = this.findCartElement();
      if (!this.cart) {
        console.error("Cart element not found. Cannot add product to cart.");
        return;
      }
    }

    const config = fetchConfig("javascript");
    config.method = "POST";
    config.headers["Content-Type"] = "application/json";

    config.body = JSON.stringify({
      id: this.input.value,
      quantity: 1,
      sections: this.cart.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (this.cart && this.cart.renderContents) {
          this.cart.renderContents(response);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (this.cart && this.cart.classList.contains("is-empty")) {
          this.cart.classList.remove("is-empty");
        }
      });
  }
}
customElements.define("pochette-cadeau", PochetteCadeau);

class BasInvisibleOffer extends HTMLElement {
  constructor() {
    super();
    this.productsData = null;
    this.selectedSize = null;
    this.selectedVariant = null;
    this.selectedProductId = null;
    this.sizeMap = new Map(); // Map<size, {variant, productId, productTitle, productIndex}>
    this.cart = null;
    this.hasSizes = false; // Nouvelle propriété pour détecter si les variants ont des tailles
  }

  connectedCallback() {
    this.loadProductsData();
    this.findCartElement();
    this.setupEventListeners();
  }

  loadProductsData() {
    const dataScript = this.querySelector('script[type="application/json"]');
    if (dataScript) {
      try {
        const data = JSON.parse(dataScript.textContent);
        this.productsData = data.offer_products_bf || [];
        console.log("Produits chargés:", this.productsData.length);

        // Log des variants par produit
        this.productsData.forEach((product, index) => {
          console.log(`Produit ${index} (${product.title}): ${product.variants ? product.variants.length : 0} variants`);
          if (product.variants) {
            product.variants.forEach((v, vIndex) => {
              console.log(`  Variant ${vIndex}: id=${v.id}, available=${v.available}, option1=${v.option1}, option2=${v.option2}`);
            });
          }
        });

        this.buildSizeMap();
        this.renderSizeOptions();
      } catch (error) {
        console.error("Erreur lors du chargement des données produits:", error);
      }
    }
  }

  findCartElement() {
    this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
    if (!this.cart) {
      setTimeout(() => {
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      }, 100);
    }
  }

  /**
   * Construit une map de toutes les tailles disponibles
   * Priorité : Produit A (index 0) > Produit B (index 1) > Produit C (index 2)
   */
  buildSizeMap() {
    console.log("=== Début buildSizeMap ===");
    this.sizeMap.clear();
    this.hasSizes = false;
    this.selectedVariant = null;
    this.selectedProductId = null;

    if (!this.productsData || this.productsData.length === 0) {
      console.error("Aucun produit chargé dans productsData");
      return;
    }

    const allAvailableVariants = [];

    this.productsData.forEach((product, productIndex) => {
      if (!product.variants || product.variants.length === 0) {
        console.warn(`Produit ${productIndex} (${product.title}) n'a aucun variant`);
        return;
      }

      product.variants.forEach((variant) => {
        const isAvailable = variant.available && (variant.inventory_policy === "continue" || variant.inventory_quantity > 0);

        if (isAvailable) {
          allAvailableVariants.push({
            variant,
            product,
            productIndex,
          });

          const size = this.getSizeFromVariant(variant);

          // Ignorer "Default Title" mais accepter tous les autres noms
          const isValidSize = size != null && String(size).trim() !== "" && String(size).trim() !== "Default Title";

          if (isValidSize) {
            this.hasSizes = true;

            if (!this.sizeMap.has(size) || productIndex < this.sizeMap.get(size).productIndex) {
              this.sizeMap.set(size, {
                variant: variant,
                productId: product.id,
                productTitle: product.title,
                productIndex: productIndex,
              });
            }
          }
        }
      });
    });

    console.log(`Variants disponibles trouvés: ${allAvailableVariants.length}`);
    console.log(`hasSizes: ${this.hasSizes}, sizeMap.size: ${this.sizeMap.size}`);

    // Si un seul variant disponible au total OU pas de tailles, sélectionner automatiquement
    if (allAvailableVariants.length === 1 || !this.hasSizes || this.sizeMap.size === 0) {
      if (allAvailableVariants.length > 0) {
        const firstAvailable = allAvailableVariants[0];
        this.selectedVariant = firstAvailable.variant;
        this.selectedProductId = firstAvailable.product.id;
        console.log("✓ Sélection automatique:", this.selectedVariant.id);

        // Forcer hasSizes à false pour masquer le sélecteur
        if (allAvailableVariants.length === 1) {
          this.hasSizes = false;
        }
      }
    }

    console.log("=== Fin buildSizeMap ===");
  }

  /**
   * Récupère la taille depuis le variant
   * Détecte automatiquement si la taille est en option1 ou option2
   */
  getSizeFromVariant(variant) {
    // Si option2 existe et n'est pas vide, c'est la taille
    if (variant.option2 != null && String(variant.option2).trim() !== "") {
      return variant.option2;
    }
    // Sinon, la taille est en option1
    if (variant.option1 != null && String(variant.option1).trim() !== "") {
      return variant.option1;
    }
    return null;
  }

  /**
   * Trie les tailles dans l'ordre (numérique et lettres)
   */
  sortSizes(sizes) {
    // Ordre de tri pour les tailles en lettres
    const sizeOrder = {
      XXS: 1,
      "2XS": 1,
      XS: 2,
      S: 3,
      M: 4,
      L: 5,
      XL: 6,
      XXL: 7,
      "2XL": 7,
      XXXL: 8,
      "3XL": 8,
      XXXXL: 9,
      "4XL": 9,
      "5XL": 10,
      "6XL": 11,
    };

    // Séparer les tailles numériques et non numériques
    const numericSizes = [];
    const letterSizes = [];
    const otherSizes = [];

    sizes.forEach((size) => {
      const raw = String(size).trim();
      const sizeStr = raw.toUpperCase();
      const isPureNumber = /^\d+$/.test(raw);

      if (isPureNumber) {
        // Taille numérique (36, 38, 40, etc.)
        numericSizes.push(size);
      } else if (sizeOrder[sizeStr] !== undefined) {
        // Taille en lettres (S, M, L, XL, etc.)
        letterSizes.push(size);
      } else {
        // Autres tailles
        otherSizes.push(size);
      }
    });

    // Trier les tailles numériques
    numericSizes.sort((a, b) => parseInt(a) - parseInt(b));

    // Trier les tailles en lettres selon l'ordre défini
    letterSizes.sort((a, b) => {
      const aUpper = String(a).trim().toUpperCase();
      const bUpper = String(b).trim().toUpperCase();
      return (sizeOrder[aUpper] || 999) - (sizeOrder[bUpper] || 999);
    });

    // Retourner les tailles dans l'ordre : numériques, lettres, autres
    return [...numericSizes, ...letterSizes, ...otherSizes];
  }

  /**
   * Affiche les options de taille
   */
  renderSizeOptions() {
    const sizesContainer = this.querySelector(".bas-invisible-sizes");
    const sizeSelector = this.querySelector(".bas-invisible-size-selector");

    if (!sizesContainer) {
      console.warn("Container .bas-invisible-sizes non trouvé");
      return;
    }

    // Si pas de tailles OU un seul variant, masquer le sélecteur
    if (!this.hasSizes || this.sizeMap.size <= 1) {
      console.log("=== renderSizeOptions: Masquage sélecteur ===");
      console.log("hasSizes:", this.hasSizes, "sizeMap.size:", this.sizeMap.size);

      if (sizeSelector) {
        sizeSelector.style.display = "none";
      }

      const addButton = this.querySelector(".bas-invisible-add-btn");
      if (!addButton) {
        console.error("✗ Bouton non trouvé");
        return;
      }

      // Si un seul variant dans la map et pas encore sélectionné, le sélectionner
      if (this.sizeMap.size === 1 && !this.selectedVariant) {
        const [size, data] = Array.from(this.sizeMap.entries())[0];
        this.selectedVariant = data.variant;
        this.selectedProductId = data.productId;
        this.selectedSize = size;
        console.log("✓ Sélection automatique du variant unique:", this.selectedVariant.id);
      }

      // Activer le bouton si un variant est sélectionné
      if (this.selectedVariant) {
        console.log("✓ Activation du bouton");
        addButton.disabled = false;
        addButton.removeAttribute("disabled");
        addButton.classList.remove("tr-opacity-50", "tr-cursor-not-allowed");
      } else {
        console.error("✗ Pas de variant sélectionné, bouton désactivé");
        addButton.disabled = true;
        addButton.setAttribute("disabled", "disabled");
        addButton.classList.add("tr-opacity-50", "tr-cursor-not-allowed");
      }

      return;
    }

    // Afficher le sélecteur de taille (reste du code existant...)
    if (sizeSelector) {
      sizeSelector.style.display = "";
    }

    const sortedSizes = this.sortSizes(Array.from(this.sizeMap.keys()));

    // Créer les inputs radio et labels de taille (même structure que variant-radios-offer)
    sizesContainer.innerHTML = "";
    sortedSizes.forEach((size, index) => {
      const data = this.sizeMap.get(size);
      const uniqueId = `bas-invisible-${this.dataset.productId || "offer"}-${size}-${index}`;

      // Créer le conteneur swiper-slide (comme dans jolly-product-variant-options-set-bundle.liquid)
      const slideDiv = document.createElement("div");
      slideDiv.className = "swiper-slide !tr-w-auto";

      // Créer l'input radio (même structure que dans jolly-product-variant-options-set-bundle.liquid ligne 98-113)
      const radioInput = document.createElement("input");
      radioInput.type = "radio";
      radioInput.id = uniqueId;
      radioInput.className = "!tr-w-[34px] tr-opacity-0 !tr-m-0 tr-z-[1] tr-not-clip tr-absolute tr-overflow-hidden !tr-h-[34px]";
      radioInput.name = `bas-invisible-size-${this.dataset.productId || "offer"}`;
      radioInput.value = size;
      radioInput.dataset.size = size;
      radioInput.dataset.variantId = data.variant.id;
      radioInput.dataset.productId = data.productId;

      // Créer le label (même structure que dans jolly-product-variant-options-set-bundle.liquid ligne 115-121)
      const label = document.createElement("label");
      label.className = "";
      label.htmlFor = uniqueId;
      label.textContent = size;

      // Ajouter un span visuellement caché pour l'accessibilité
      const hiddenSpan = document.createElement("span");
      hiddenSpan.className = "visually-hidden";
      hiddenSpan.textContent = "products.product.variant_sold_out_or_unavailable";
      label.appendChild(hiddenSpan);

      // Ajouter l'événement de changement
      radioInput.addEventListener("change", () => this.selectSize(radioInput, data));

      // Fallback click handler : Swiper peut intercepter les events change sur les slides.
      // On force la selection sur click du slide entier si le radio n'est pas deja checked.
      slideDiv.addEventListener("click", () => {
        if (!radioInput.checked) {
          radioInput.checked = true;
          this.selectSize(radioInput, data);
        }
      });

      // Assembler les éléments (input puis label, comme dans le snippet Liquid)
      slideDiv.appendChild(radioInput);
      slideDiv.appendChild(label);
      sizesContainer.appendChild(slideDiv);
    });

    // Initialiser le swiper après avoir créé tous les slides
    this.initSwiper();
  }

  /**
   * Initialise le swiper pour les tailles
   */
  initSwiper() {
    const swiperContainer = this.querySelector(".variants-swiper");
    if (!swiperContainer || !swiperContainer.id) {
      return;
    }

    // Vérifier si le swiper existe déjà et le détruire
    if (swiperContainer.swiperInstance) {
      swiperContainer.swiperInstance.destroy(true, true);
    }

    const swiperEl = swiperContainer.querySelector(".swiper");
    if (!swiperEl) {
      return;
    }

    // Vérifier si Swiper est disponible globalement
    if (typeof Swiper === "undefined") {
      console.warn("Swiper n'est pas disponible");
      return;
    }

    try {
      const swiperInstance = new Swiper(swiperEl, {
        modules: typeof Navigation !== "undefined" ? [Navigation] : [],
        slidesPerView: 2,
        spaceBetween: 8,
        noSwiping: false,
        slideToClickedSlide: false,
        navigation: {
          nextEl: swiperEl.nextElementSibling,
          prevEl: swiperEl.nextElementSibling?.nextElementSibling,
        },
      });

      // Stocker l'instance pour pouvoir la détruire plus tard
      swiperContainer.swiperInstance = swiperInstance;
    } catch (error) {
      console.error("Erreur lors de l'initialisation du swiper:", error);
    }
  }

  /**
   * Gère la sélection d'une taille
   */
  selectSize(radioInput, data) {
    // Désélectionner les autres inputs radio
    this.querySelectorAll('input[type="radio"][name^="bas-invisible-size"]').forEach((input) => {
      input.checked = false;
    });

    // Sélectionner l'input radio cliqué
    radioInput.checked = true;

    this.selectedSize = data.variant.option2;
    this.selectedVariant = data.variant;
    this.selectedProductId = data.productId;

    // Activer le bouton d'ajout
    const addButton = this.querySelector(".bas-invisible-add-btn");
    if (addButton) {
      addButton.disabled = false;
      addButton.classList.remove("tr-opacity-50", "tr-cursor-not-allowed");
    }
  }

  setupEventListeners() {
    const addButton = this.querySelector(".bas-invisible-add-btn");
    if (addButton) {
      addButton.addEventListener("click", () => this.addToCart());
    }
  }

  /**
   * Ajoute le produit au panier
   */
  async addToCart() {
    console.log("=== addToCart appelé ===");
    console.log("hasSizes:", this.hasSizes);
    console.log("selectedVariant:", this.selectedVariant?.id);
    console.log("cart:", this.cart ? "trouvé" : "NON TROUVÉ");

    // Vérifier que le cart est trouvé, sinon essayer de le retrouver
    if (!this.cart) {
      console.warn("Cart non trouvé, tentative de récupération...");
      this.findCartElement();
      if (!this.cart) {
        console.error("✗ Cart non trouvé après tentative de récupération");
        alert("Impossible de trouver le panier. Veuillez rafraîchir la page.");
        return;
      }
    }

    // Vérifier qu'on a un variant sélectionné
    if (!this.selectedVariant) {
      console.error("✗ Aucun variant sélectionné");
      console.error("  hasSizes:", this.hasSizes);
      console.error("  sizeMap.size:", this.sizeMap.size);
      console.error("  productsData:", this.productsData?.length, "produits");
      alert("Aucun variant disponible. Veuillez vérifier la configuration du produit.");
      return;
    }

    console.log("✓ Toutes les vérifications passées, ajout au panier...");

    const addButton = this.querySelector(".bas-invisible-add-btn");
    if (!addButton) return;

    // Désactiver le bouton et afficher le loader
    addButton.disabled = true;
    addButton.classList.add("loading");
    const spinner = addButton.querySelector(".loading-overlay__spinner");
    if (spinner) spinner.classList.remove("hidden");

    const config = fetchConfig("javascript");
    config.method = "POST";
    config.headers["Content-Type"] = "application/json";

    config.body = JSON.stringify({
      id: this.selectedVariant.id,
      quantity: 1,
      sections: this.cart.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();

      if (data.errors) {
        console.error("Erreur lors de l'ajout au panier:", data.errors);
        alert("Une erreur est survenue lors de l'ajout au panier");
        return;
      }

      if (this.cart && this.cart.renderContents) {
        this.cart.renderContents(data);
      }

      // Réinitialiser la sélection
      this.resetSelection();
    } catch (error) {
      console.error("Erreur lors de l'ajout au panier:", error);
      alert("Une erreur est survenue lors de l'ajout au panier");
    } finally {
      // Réactiver le bouton
      addButton.disabled = false;
      addButton.classList.remove("loading");
      const spinner = addButton.querySelector(".loading-overlay__spinner");
      if (spinner) spinner.classList.add("hidden");
    }
  }

  /**
   * Réinitialise la sélection
   */
  resetSelection() {
    this.selectedSize = null;

    // Si pas de tailles, garder le variant sélectionné pour permettre un nouvel ajout
    if (this.hasSizes) {
      this.selectedVariant = null;
      this.selectedProductId = null;
    }

    // Désélectionner tous les inputs radio
    this.querySelectorAll('input[type="radio"][name^="bas-invisible-size"]').forEach((input) => {
      input.checked = false;
    });

    const addButton = this.querySelector(".bas-invisible-add-btn");
    if (addButton) {
      // Si pas de tailles, garder le bouton activé
      if (!this.hasSizes && this.selectedVariant) {
        addButton.disabled = false;
        addButton.classList.remove("tr-opacity-50", "tr-cursor-not-allowed");
      } else {
        addButton.disabled = true;
        addButton.classList.add("tr-opacity-50", "tr-cursor-not-allowed");
      }
    }
  }
}

customElements.define("bas-invisible-offer", BasInvisibleOffer);

class CartGiftCarousel extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
  }

  connectedCallback() {
    this.findCartElement();
    this.querySelectorAll(".cart-gift-carousel__add-btn").forEach((btn) => {
      btn.addEventListener("click", (event) => this.addToCart(event));
    });
  }

  findCartElement() {
    this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
  }

  async addToCart(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId) {
      console.error("CartGiftCarousel: variant id manquant");
      return;
    }

    if (!this.cart) this.findCartElement();
    if (!this.cart) {
      console.error("CartGiftCarousel: cart introuvable");
      return;
    }

    if (button.disabled) return;
    button.disabled = true;
    button.classList.add("loading");
    const spinner = button.querySelector(".loading-overlay__spinner");
    if (spinner) spinner.classList.remove("hidden");

    const config = fetchConfig("javascript");
    config.method = "POST";
    config.headers["Content-Type"] = "application/json";
    config.body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: this.cart.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();

      if (data.errors) {
        console.error("CartGiftCarousel: erreur d'ajout", data.errors);
        return;
      }

      if (this.cart && this.cart.renderContents) {
        this.cart.renderContents(data);
      }
    } catch (error) {
      console.error("CartGiftCarousel: erreur reseau", error);
    } finally {
      button.disabled = false;
      button.classList.remove("loading");
      if (spinner) spinner.classList.add("hidden");
    }
  }
}

customElements.define("cart-gift-carousel", CartGiftCarousel);

class LangPopup extends HTMLElement {
  constructor() {
    super();

    this.closeButton = this.querySelector("#tr-lang-popup-close");
    this.closeButton2 = this.querySelector("#tr-lang-popup-btn-close");

    this.content = this.querySelector("#tr-lang-popup-content");
    this.validateButton = this.querySelector("#tr-lang-popup-validate");
    this.form = this.querySelector("form");
    this.langPopupBtn = document.querySelector("#tr-lang-popupBtn");
    this.langPopupBtnMobile = document.querySelector("#tr-lang-popupBtn-mobile");

    this.step1 = this.querySelector("#tr-lang-popup-step-1");
    this.btnToStep2 = this.querySelector("#tr-lang-popup-step-2-btn");
    this.step2 = this.querySelector("#tr-lang-popup-step-2");

    this.langSuggestion();
  }

  connectedCallback() {
    this.closeButton.addEventListener("click", this.close.bind(this));
    this.closeButton2.addEventListener("click", this.close.bind(this));
    this.validateButton.addEventListener("click", this.onSubmit.bind(this));

    // Ajout de l'écouteur pour le bouton d'ouverture
    if (this.langPopupBtn) {
      this.langPopupBtn.addEventListener("click", this.open.bind(this));
    }

    if (this.langPopupBtnMobile) {
      this.langPopupBtnMobile.addEventListener("click", this.open.bind(this));
    }

    if (this.btnToStep2) {
      this.btnToStep2.addEventListener("click", this.openStep2.bind(this));
    }

    // Gestion du clic en dehors du contenu
    document.addEventListener("click", (event) => {
      if (this.classList.contains("tr-lang-popup--open") && !this.content.contains(event.target) && !this.langPopupBtn.contains(event.target) && !this.langPopupBtnMobile.contains(event.target)) {
        this.close();
      }
    });
  }

  // Open the lang popup
  open() {
    this.classList.add("tr-lang-popup--open");
  }

  // Close the lang popup
  close() {
    this.classList.remove("tr-lang-popup--open");
    this.closeStep2();
  }

  openStep2() {
    this.step1.classList.add("tr-hidden");
    this.step2.classList.remove("tr-hidden");
    this.step2.classList.add("tr-flex");
  }

  closeStep2() {
    this.step2.classList.add("tr-hidden");
    this.step2.classList.remove("tr-flex");
    this.step1.classList.remove("tr-hidden");
  }

  // Get the lang suggestion
  async langSuggestion() {
    return await fetch(window.Shopify.routes.root + "browsing_context_suggestions.json" + "?country[enabled]=true" + `&country[exclude]=${window.Shopify.country}` + "&language[enabled]=true" + `&language[exclude]=${window.Shopify.locale}`).then((r) => r.json());
  }

  // Show lang popup if there is a lang suggestion different from the current lang
  showLangPopup() {
    // Vérifier si le localStorage existe déjà
    if (localStorage.getItem("lang_popup_shown")) {
      return;
    }

    this.langSuggestion().then((response) => {
      console.log(response);
      if (response.suggestions.length > 0) {
        //Langue suggestions
        const suggestedLanguage = response.suggestions[0].parts.language ? response.suggestions[0].parts.language.handle : null;
        const currentLocale = window.Shopify.locale;

        console.log(suggestedLanguage, currentLocale);

        // Si la langue suggérée est différente de la langue actuelle
        if (suggestedLanguage !== currentLocale) {
          this.open();
        }

        //Pays suggestions
        const suggestedCountry = response.suggestions[0].parts.country ? response.suggestions[0].parts.country.handle : null;
        const currentCountry = window.Shopify.country;

        console.log(suggestedCountry, currentCountry);

        if (suggestedCountry !== currentCountry) {
          this.open();
        }
      }
    });
  }

  // On submit the lang popup
  onSubmit(event) {
    event.preventDefault();

    if (this.form) {
      // Utiliser localStorage au lieu du cookie
      localStorage.setItem("lang_popup_shown", "true");
      this.form.submit();
      this.loader();
    }
  }

  loader() {
    this.validateButton.classList.add("tr-loading");
    this.validateButton.setAttribute("aria-disabled", true);
    this.validateButton.setAttribute("disabled", true);
  }
}
customElements.define("lang-popup", LangPopup);
